import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { api, ApiError } from '../../services/api';
import {
  VAT_QUESTION,
  VAT_NOT_REGISTERED_NOTE,
} from '../../components/onboarding/VatDeclarationSection';

// Spec 051 AC-19 — Partner Settings VAT registration block.
// View mode shows the declared status; edit mode mirrors the onboarding question,
// but here a 15-digit TRN IS required when "Yes" is selected (the backend enforces
// the same rule with 422 TRN_REQUIRED_FOR_VAT_REGISTERED).

interface VatState {
  vatRegistered: boolean | null;
  trn: string | null;
  vatRegisteredFrom: string | null;
  vatStatusDeclaredAt: string | null;
}

const fmtDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

export default function VatRegistrationSection() {
  const { token } = useAuth();

  const [current, setCurrent] = useState<VatState | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit-mode form state
  const [formRegistered, setFormRegistered] = useState<boolean | null>(null);
  const [formTrn, setFormTrn] = useState('');
  const [formFrom, setFormFrom] = useState('');

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [questionError, setQuestionError] = useState('');
  const [trnError, setTrnError] = useState('');

  useEffect(() => {
    if (!token) return;
    api.getMyGarage(token)
      .then((g) => {
        setCurrent({
          vatRegistered: g.vatRegistered ?? null,
          trn: g.trn ?? null,
          vatRegisteredFrom: g.vatRegisteredFrom ?? null,
          vatStatusDeclaredAt: g.vatStatusDeclaredAt ?? null,
        });
      })
      .catch(() => setCurrent(null))
      .finally(() => setLoading(false));
  }, [token]);

  const startEdit = () => {
    if (!current) return;
    setFormRegistered(current.vatStatusDeclaredAt ? (current.vatRegistered ?? false) : null);
    setFormTrn(current.trn ?? '');
    setFormFrom(current.vatRegisteredFrom ?? '');
    setSuccess('');
    setError('');
    setQuestionError('');
    setTrnError('');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!token) return;
    setQuestionError('');
    setTrnError('');
    setError('');
    setSuccess('');

    if (formRegistered === null) {
      setQuestionError('Please select Yes or No.');
      return;
    }
    // Unlike onboarding, the TRN is REQUIRED here when the garage is VAT-registered
    // (mirrors the backend's 422 TRN_REQUIRED_FOR_VAT_REGISTERED).
    if (formRegistered && !/^[0-9]{15}$/.test(formTrn)) {
      setTrnError('A 15-digit TRN is required when your garage is VAT-registered.');
      return;
    }

    setSaving(true);
    try {
      const res = await api.updateMyVat(
        {
          vatRegistered: formRegistered,
          trn: formRegistered ? formTrn : null,
          vatRegisteredFrom: formRegistered && formFrom !== '' ? formFrom : null,
        },
        token,
      );
      setCurrent({
        vatRegistered: res.vatRegistered ?? formRegistered,
        trn: res.trn ?? null,
        vatRegisteredFrom: res.vatRegisteredFrom ?? null,
        vatStatusDeclaredAt: res.vatStatusDeclaredAt ?? new Date().toISOString(),
      });
      setSuccess('VAT registration details saved.');
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiError) {
        const trnFieldError = err.fieldErrors?.trn;
        if (err.code === 'TRN_REQUIRED_FOR_VAT_REGISTERED' || trnFieldError) {
          setTrnError(
            err.code === 'TRN_REQUIRED_FOR_VAT_REGISTERED'
              ? 'A 15-digit TRN is required when your garage is VAT-registered.'
              : `TRN: ${trnFieldError}`,
          );
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Could not save your VAT details.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-section">
        <h3 className="settings-section-title">VAT registration</h3>
        <p>Loading…</p>
      </div>
    );
  }

  if (!current) return null; // garage unavailable — don't break the tab

  const declared = current.vatStatusDeclaredAt != null;
  const isRegistered = declared && current.vatRegistered === true;

  return (
    <div className="settings-section" data-testid="vat-registration-section">
      <div className="settings-section-header">
        <h3 className="settings-section-title">VAT registration</h3>
        {!editing && (
          <button type="button" className="settings-btn-primary" onClick={startEdit}>
            {declared ? 'Edit' : 'Declare now'}
          </button>
        )}
      </div>

      {success && <div className="settings-toast settings-toast-success">{success}</div>}
      {error && <div className="settings-toast settings-toast-error">{error}</div>}

      {!editing && (
        <div className="vat-settings-view">
          {!declared && (
            <p className="vat-settings-status">
              Your VAT registration status has not been declared yet. It is required before
              your garage can be approved and listed on Garagat.
            </p>
          )}
          {declared && isRegistered && (
            <>
              <p className="vat-settings-status">
                <strong>Registered for VAT.</strong>
              </p>
              <p className="vat-settings-line">TRN: {current.trn || '— not provided yet'}</p>
              {current.vatRegisteredFrom && (
                <p className="vat-settings-line">VAT registered from: {fmtDate(current.vatRegisteredFrom)}</p>
              )}
              {current.vatStatusDeclaredAt && (
                <p className="vat-settings-line muted">Declared on {fmtDate(current.vatStatusDeclaredAt)}</p>
              )}
            </>
          )}
          {declared && !isRegistered && (
            <>
              <p className="vat-settings-status">
                <strong>Not registered for VAT.</strong>
              </p>
              {current.vatStatusDeclaredAt && (
                <p className="vat-settings-line muted">Declared on {fmtDate(current.vatStatusDeclaredAt)}</p>
              )}
            </>
          )}
          {declared && !isRegistered && (
            <div className="vat-note vat-note-warning" role="note">
              {VAT_NOT_REGISTERED_NOTE}
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="settings-form">
          <div
            className="settings-form-row"
            role="radiogroup"
            aria-label={VAT_QUESTION}
            aria-invalid={questionError ? 'true' : undefined}
          >
            <label>{VAT_QUESTION} *</label>
            <div className="vat-radio-row">
              <label className="vat-radio-option">
                <input
                  type="radio"
                  name="settings-vat-registered"
                  checked={formRegistered === true}
                  onChange={() => { setFormRegistered(true); setQuestionError(''); }}
                />
                <span>Yes</span>
              </label>
              <label className="vat-radio-option">
                <input
                  type="radio"
                  name="settings-vat-registered"
                  checked={formRegistered === false}
                  onChange={() => { setFormRegistered(false); setQuestionError(''); setTrnError(''); }}
                />
                <span>No</span>
              </label>
            </div>
            {questionError && <span className="field-error" role="alert">{questionError}</span>}
          </div>

          {formRegistered === true && (
            <>
              <div className="settings-form-row">
                <label htmlFor="settings-vat-trn">TRN (Tax Registration Number) *</label>
                <input
                  id="settings-vat-trn"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={15}
                  placeholder="15-digit TRN"
                  value={formTrn}
                  onChange={(e) => {
                    setFormTrn(e.target.value.replace(/[^0-9]/g, '').slice(0, 15));
                    setTrnError('');
                  }}
                />
                {trnError && <span className="field-error" role="alert">{trnError}</span>}
              </div>
              <div className="settings-form-row">
                <label htmlFor="settings-vat-from">VAT registered from (optional)</label>
                <input
                  id="settings-vat-from"
                  type="date"
                  value={formFrom}
                  onChange={(e) => setFormFrom(e.target.value)}
                />
              </div>
            </>
          )}

          {formRegistered === false && (
            <div className="vat-note vat-note-warning" role="note">
              {VAT_NOT_REGISTERED_NOTE}
            </div>
          )}

          <div className="vat-settings-actions">
            <button
              type="button"
              className="settings-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="vat-btn-secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
